import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Badge } from '../components/ui/badge';
import { RiskBadge } from '../components/aml/RiskBadge';
import { Shield, Plus, Filter, CheckCircle, Clock } from 'lucide-react';
import { toast } from 'sonner';
import { amlAPI } from '../lib/amlAPI';
import { clientsAPI } from '../lib/clientsAPI';
import { formatAUDate } from '../lib/dateUtils';

export default function RiskAssessment() {
  const navigate = useNavigate();
  const [assessments, setAssessments] = useState([]);
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showDialog, setShowDialog] = useState(false);
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterRiskLevel, setFilterRiskLevel] = useState('all');

  // Form state
  const [selectedClientId, setSelectedClientId] = useState('');
  const [selectedFactors, setSelectedFactors] = useState([]);
  const [notes, setNotes] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const riskFactors = [
    { id: 'high_risk_country', label: 'High Risk Country/Jurisdiction', weight: 25 },
    { id: 'pep_status', label: 'Politically Exposed Person (PEP)', weight: 30 },
    { id: 'complex_structure', label: 'Complex Corporate Structure', weight: 15 },
    { id: 'cash_intensive', label: 'Cash-Intensive Business', weight: 20 },
    { id: 'high_value_transactions', label: 'High Value Transactions', weight: 15 },
    { id: 'offshore_entities', label: 'Offshore Entities/Accounts', weight: 25 },
    { id: 'inconsistent_activity', label: 'Inconsistent Business Activity', weight: 20 },
    { id: 'new_client', label: 'New Client (<12 months)', weight: 10 },
    { id: 'industry_high_risk', label: 'High-Risk Industry', weight: 20 }
  ];

  useEffect(() => {
    loadData();
  }, [filterStatus, filterRiskLevel]);

  const loadData = async () => {
    try {
      const [assessmentsRes, clientsRes] = await Promise.all([
        amlAPI.listRiskAssessments({
          status: filterStatus !== 'all' ? filterStatus : undefined,
          risk_level: filterRiskLevel !== 'all' ? filterRiskLevel : undefined
        }),
        clientsAPI.list()
      ]);
      setAssessments(assessmentsRes.data || []);
      setClients(clientsRes.data || []);
    } catch (error) {
      toast.error('Failed to load risk assessments');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const calculatePreviewScore = () => {
    const weights = {
      high_risk_country: 25, pep_status: 30, complex_structure: 15,
      cash_intensive: 20, high_value_transactions: 15, offshore_entities: 25,
      inconsistent_activity: 20, new_client: 10, industry_high_risk: 20
    };
    const score = selectedFactors.reduce((sum, factor) => sum + (weights[factor] || 10), 0);
    const cappedScore = Math.min(score, 100);
    
    let riskLevel = 'low';
    if (cappedScore >= 60) riskLevel = 'high';
    else if (cappedScore >= 30) riskLevel = 'medium';
    
    return { score: cappedScore, level: riskLevel };
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!selectedClientId) {
      toast.error('Please select a client');
      return;
    }
    
    if (selectedFactors.length === 0) {
      toast.error('Please select at least one risk factor');
      return;
    }

    setSubmitting(true);
    try {
      await amlAPI.createRiskAssessment({
        client_id: selectedClientId,
        risk_factors: selectedFactors,
        notes: notes || undefined
      });
      
      toast.success('Risk assessment created successfully');
      setShowDialog(false);
      resetForm();
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create risk assessment');
    } finally {
      setSubmitting(false);
    }
  };

  const handleApprove = async (id) => {
    try {
      await amlAPI.approveRiskAssessment(id);
      toast.success('Risk assessment approved');
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to approve assessment');
    }
  };

  const resetForm = () => {
    setSelectedClientId('');
    setSelectedFactors([]);
    setNotes('');
  };

  const toggleFactor = (factorId) => {
    setSelectedFactors(prev =>
      prev.includes(factorId)
        ? prev.filter(id => id !== factorId)
        : [...prev, factorId]
    );
  };

  const preview = calculatePreviewScore();

  if (loading) {
    return (
      <div className="p-6">
        <div className="animate-pulse space-y-6">
          <div className="h-8 bg-muted rounded w-1/4"></div>
          <div className="grid grid-cols-3 gap-6">
            {[...Array(3)].map((_, i) => (
              <div key={i} className="h-48 bg-muted rounded"></div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-foreground" style={{fontFamily: 'Plus Jakarta Sans, sans-serif'}} data-testid="risk-assessment-title">
            Risk Assessment
          </h1>
          <p className="text-muted-foreground mt-1">Assess and manage client risk levels</p>
        </div>
        <Dialog open={showDialog} onOpenChange={setShowDialog}>
          <DialogTrigger asChild>
            <Button className="bg-primary hover:bg-primary/90" data-testid="new-assessment-button">
              <Plus className="w-4 h-4 mr-2" />
              New Assessment
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle style={{fontFamily: 'Plus Jakarta Sans, sans-serif'}}>Create Risk Assessment</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-6 mt-4">
              <div className="space-y-2">
                <Label htmlFor="client">Client *</Label>
                <Select value={selectedClientId} onValueChange={setSelectedClientId}>
                  <SelectTrigger id="client" data-testid="assessment-client-select">
                    <SelectValue placeholder="Select a client" />
                  </SelectTrigger>
                  <SelectContent>
                    {clients.map((client) => (
                      <SelectItem key={client.id} value={client.id}>
                        {client.display_name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-3">
                <Label>Risk Factors *</Label>
                <div className="space-y-2 max-h-64 overflow-y-auto border rounded-lg p-4">
                  {riskFactors.map((factor) => (
                    <label
                      key={factor.id}
                      className="flex items-start gap-3 p-3 hover:bg-muted/50 rounded cursor-pointer transition-colors"
                    >
                      <input
                        type="checkbox"
                        checked={selectedFactors.includes(factor.id)}
                        onChange={() => toggleFactor(factor.id)}
                        className="mt-1"
                        data-testid={`risk-factor-${factor.id}`}
                      />
                      <div className="flex-1">
                        <div className="font-medium text-sm text-foreground">{factor.label}</div>
                        <div className="text-xs text-muted-foreground">Weight: {factor.weight} points</div>
                      </div>
                    </label>
                  ))}
                </div>
              </div>

              {selectedFactors.length > 0 && (
                <div className="p-4 bg-muted/50 rounded-lg border">
                  <h4 className="font-semibold text-sm text-foreground mb-2">Risk Preview</h4>
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-muted-foreground">Calculated Risk Score:</span>
                    <div className="flex items-center gap-3">
                      <span className="text-2xl font-bold text-foreground">{preview.score}</span>
                      <RiskBadge riskLevel={preview.level} />
                    </div>
                  </div>
                </div>
              )}

              <div className="space-y-2">
                <Label htmlFor="notes">Notes</Label>
                <Textarea
                  id="notes"
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  placeholder="Additional notes or context for this assessment"
                  rows={4}
                  data-testid="assessment-notes"
                />
              </div>

              <div className="flex gap-3 pt-4">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setShowDialog(false)}
                  disabled={submitting}
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  className="bg-primary hover:bg-primary/90"
                  disabled={submitting || !selectedClientId || selectedFactors.length === 0}
                  data-testid="submit-assessment-button"
                >
                  {submitting ? 'Creating...' : 'Create Assessment'}
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="pt-6">
          <div className="flex items-center gap-4">
            <div className="flex items-center gap-2">
              <Filter className="w-4 h-4 text-muted-foreground" />
              <span className="text-sm font-medium text-foreground/90">Filters:</span>
            </div>
            <Select value={filterStatus} onValueChange={setFilterStatus}>
              <SelectTrigger className="w-40" data-testid="filter-status">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Status</SelectItem>
                <SelectItem value="draft">Draft</SelectItem>
                <SelectItem value="approved">Approved</SelectItem>
              </SelectContent>
            </Select>
            <Select value={filterRiskLevel} onValueChange={setFilterRiskLevel}>
              <SelectTrigger className="w-40" data-testid="filter-risk-level">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Risk Levels</SelectItem>
                <SelectItem value="low">Low Risk</SelectItem>
                <SelectItem value="medium">Medium Risk</SelectItem>
                <SelectItem value="high">High Risk</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Assessments List */}
      {assessments.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center">
            <Shield className="w-16 h-16 mx-auto mb-4 text-muted-foreground/50" />
            <h3 className="text-lg font-semibold text-foreground mb-2">No Risk Assessments Yet</h3>
            <p className="text-muted-foreground mb-4">Start by creating your first client risk assessment</p>
            <Button onClick={() => setShowDialog(true)} className="bg-primary hover:bg-primary/90">
              <Plus className="w-4 h-4 mr-2" />
              Create Assessment
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
          {assessments.map((assessment) => (
            <Card key={assessment.id} className="hover:shadow-md transition-shadow" data-testid={`assessment-card-${assessment.id}`}>
              <CardContent className="pt-6">
                <div className="space-y-4">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <h3 className="font-semibold text-foreground mb-1">{assessment.client_name}</h3>
                      <p className="text-xs text-muted-foreground">Assessed by {assessment.assessor_name}</p>
                    </div>
                    <RiskBadge riskLevel={assessment.risk_level} />
                  </div>

                  <div className="flex items-center justify-between py-3 border-y">
                    <span className="text-sm text-muted-foreground">Risk Score</span>
                    <span className="text-2xl font-bold text-foreground">{assessment.risk_score}</span>
                  </div>

                  <div className="space-y-2">
                    <div className="text-xs font-medium text-muted-foreground uppercase">Risk Factors ({assessment.risk_factors.length})</div>
                    <div className="flex flex-wrap gap-1">
                      {assessment.risk_factors.slice(0, 3).map((factor, idx) => (
                        <Badge key={idx} variant="outline" className="text-xs">
                          {factor.replace(/_/g, ' ')}
                        </Badge>
                      ))}
                      {assessment.risk_factors.length > 3 && (
                        <Badge variant="outline" className="text-xs">+{assessment.risk_factors.length - 3} more</Badge>
                      )}
                    </div>
                  </div>

                  <div className="flex items-center justify-between text-xs text-muted-foreground">
                    <span>{formatAUDate(assessment.assessment_date)}</span>
                    {assessment.status === 'draft' ? (
                      <Badge className="bg-amber-100 text-amber-800">Draft</Badge>
                    ) : (
                      <Badge className="bg-green-100 text-green-800">
                        <CheckCircle className="w-3 h-3 mr-1" />
                        Approved
                      </Badge>
                    )}
                  </div>

                  {assessment.status === 'draft' && (
                    <Button
                      onClick={() => handleApprove(assessment.id)}
                      size="sm"
                      className="w-full bg-primary hover:bg-primary/90"
                      data-testid={`approve-assessment-${assessment.id}`}
                    >
                      <CheckCircle className="w-4 h-4 mr-2" />
                      Approve Assessment
                    </Button>
                  )}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}